/**
 * Microsoft Purview eDiscovery search queries (KeyQL), built from the parts the
 * server suggests for a case's request (Lake Stevens R2-12).
 *
 * The model never writes KeyQL: it returns the parts (POST /cases/{id}/purview-query)
 * and this module builds the query, so the syntax is always valid and every
 * edit rebuilds it live. Purview's rules it follows (Microsoft Learn,
 * edisc-keyword-query-language / edisc-ref-limits):
 * - AND / OR / NOT / NEAR are upper case; a lower-case "or" is a word.
 * - A phrase is in double quotes; quotes can't be nested, so terms lose theirs.
 * - A wildcard is a prefix match only: at the end of one word, 3+ letters before it.
 * - A query has at most 10,000 characters and 100 operators.
 * - 25 participants (50 with eDiscovery Premium).
 * - Mailboxes and sites are chosen with "Add sources" in Purview, not in the query.
 */

export interface PurviewKeywordGroup {
  label: string;
  terms: string[];
  reason: string;
}

export interface PurviewQueryParts {
  keyword_groups: PurviewKeywordGroup[];
  /** Email only. Values in a side are OR'd; both sides must take part. */
  participants: { side_a: string[]; side_b: string[]; reason: string };
  /** Words documents (which have no participants) must mention instead. */
  document_terms: { terms: string[]; reason: string };
  /** YYYY-MM-DD. Email by `sent`, documents by `lastmodifiedtime`. */
  date_range: { start: string | null; end: string | null; reason: string };
  item_types: { email: boolean; documents: boolean; meetings: boolean; reason: string };
  /** Words whose items to leave out, only when the request itself excludes something. */
  exclusions: { terms: string[]; reason: string };
  /** How the narrower variant differs from the focused query. */
  narrower: { keywords_in_subject: boolean; email_only: boolean; require_terms: string[]; reason: string };
}

export interface PurviewQuerySuggestion {
  parts: PurviewQueryParts;
  /** "llm": the internal LLM; "rules": the fallback when it was unavailable. */
  source: 'llm' | 'rules';
}

/** The common noise exclusions, off by default: `subject:` matches any subject containing the words. */
export interface StandardExclusions {
  autoReplies: boolean;
  outOfOffice: boolean;
  newsletters: boolean;
}

export const NO_STANDARD_EXCLUSIONS: StandardExclusions = { autoReplies: false, outOfOffice: false, newsletters: false };

export const STANDARD_EXCLUSION_CLAUSES: Record<keyof StandardExclusions, string> = {
  autoReplies: 'subject:"Automatic reply"',
  outOfOffice: 'subject:"Out of office"',
  newsletters: 'subject:newsletter',
};

export type QueryVariant = 'focused' | 'narrower';

export const PURVIEW_LIMITS = {
  chars: 10_000,
  operators: 100,
  participants: 25,
  participantsPremium: 50,
  /** Warn from this share of a limit. */
  nearShare: 0.9,
} as const;

export interface QueryWarning {
  level: 'warning' | 'error';
  message: string;
}

export interface BuiltQuery {
  keyql: string;
  chars: number;
  operators: number;
  participants: number;
  warnings: QueryWarning[];
  /** What each part adds to the query, to show under it ('' when it adds nothing). */
  fragments: {
    keywords: string[];
    people: string;
    documents: string;
    dates: string;
    types: string;
    exclusions: string;
  };
}

const RESERVED = new Set(['AND', 'OR', 'NOT', 'NEAR', 'ONEAR', 'XRANK', 'TO']);
const QUOTES = /["“”„‟«»]/g;
const BARE_WORD = /^[\p{L}\p{N}_]+$/u;
const EMAIL = /^[^\s@"]+@[^\s@".]+(\.[^\s@".]+)+$/;
const DOMAIN = /^([a-z0-9-]+\.)+[a-z]{2,}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "a, b; c\nd" → ["a", "b", "c", "d"], trimmed, without blanks or repeats (any case). */
export function splitTerms(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,;\n]/)) {
    const term = raw.replace(/\s+/g, ' ').trim();
    if (term && !seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      out.push(term);
    }
  }
  return out;
}

/**
 * One keyword as KeyQL: a single word bare, anything else as an exact phrase.
 * Quotes inside are dropped (no nesting); a wildcard that Purview can't run is
 * dropped with a warning.
 */
export function formatTerm(raw: string): { text: string | null; warning?: string } {
  let term = raw.replace(QUOTES, ' ').replace(/\s+/g, ' ').trim();
  if (!term) return { text: null };
  let warning: string | undefined;
  if (term.includes('*')) {
    const stem = term.slice(0, -1);
    if (term.endsWith('*') && BARE_WORD.test(stem) && [...stem].length >= 3) {
      return { text: term };
    }
    const without = term.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    warning = `“${term}”: a wildcard goes at the end of one word, with at least 3 letters before it. It is searched as “${without}”.`;
    term = without;
    if (!term) return { text: null, warning };
  }
  const bare = BARE_WORD.test(term) && !RESERVED.has(term.toUpperCase());
  return { text: bare ? term : `"${term}"`, warning };
}

export function isEmailOrDomain(value: string): boolean {
  return EMAIL.test(value) || DOMAIN.test(value);
}

/** `participants:` for an email address or a domain; a name is quoted with a warning. */
export function formatParticipant(raw: string): { text: string | null; warning?: string } {
  const value = raw.replace(QUOTES, ' ').replace(/\s+/g, ' ').trim();
  if (!value) return { text: null };
  if (isEmailOrDomain(value)) return { text: `participants:${value.toLowerCase()}` };
  return {
    text: `participants:"${value}"`,
    warning: `“${value}” is a name, not an email address or domain, so Purview may not match it. Type the address if you know it.`,
  };
}

export function isValidIsoDate(value: string | null | undefined): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/** "a" or "(a OR b)"; '' for none. */
export function orGroup(items: string[]): string {
  const list = items.filter(Boolean);
  if (list.length === 0) return '';
  return list.length === 1 ? list[0] : `(${list.join(' OR ')})`;
}

function andAll(items: string[]): string {
  return items.filter(Boolean).join(' AND ');
}

function wrap(clause: string): string {
  return / (AND|OR) /.test(clause) && !(clause.startsWith('(') && closesAtEnd(clause)) ? `(${clause})` : clause;
}

/** True when the "(" at index 0 closes at the last character. */
function closesAtEnd(clause: string): boolean {
  let depth = 0;
  let quoted = false;
  for (let i = 0; i < clause.length; i++) {
    const c = clause[i];
    if (c === '"') quoted = !quoted;
    if (quoted) continue;
    if (c === '(') depth++;
    if (c === ')') {
      depth--;
      if (depth === 0 && i < clause.length - 1) return false;
    }
  }
  return true;
}

/** AND / OR / NOT / NEAR / ONEAR outside quotes (Purview counts at most 100). */
export function countOperators(keyql: string): number {
  const unquoted = keyql.replace(/"[^"]*"/g, ' ');
  return (unquoted.match(/\b(AND|OR|NOT|NEAR|ONEAR)\b/g) ?? []).length;
}

function dateClauses(property: string, start: string | null, end: string | null): string[] {
  const out: string[] = [];
  if (isValidIsoDate(start)) out.push(`${property}>=${start}`);
  if (isValidIsoDate(end)) out.push(`${property}<=${end}`);
  return out;
}

/**
 * The KeyQL for the parts. "focused": the keywords anywhere, and either email
 * between the participants or documents that mention the document terms, in
 * the date range. "narrower": the same parts with the narrower options
 * (keywords in the subject line, email only, one more required word).
 */
export function buildKeyQL(
  parts: PurviewQueryParts,
  standard: StandardExclusions = NO_STANDARD_EXCLUSIONS,
  variant: QueryVariant = 'focused',
): BuiltQuery {
  const warnings: string[] = [];
  const note = (w?: string) => {
    if (w && !warnings.includes(w)) warnings.push(w);
  };
  const terms = (list: string[]) =>
    list.map((t) => {
      const f = formatTerm(t);
      note(f.warning);
      return f.text;
    }).filter((t): t is string => Boolean(t));

  const narrow = variant === 'narrower';
  const { item_types: kinds, date_range: dates, narrower } = parts;

  // Keywords: each group is one OR group; every group must match.
  const groupTerms = parts.keyword_groups.map((g) => terms(g.terms));
  const keywordFragments = groupTerms.map(orGroup);
  const plainKeywords = keywordFragments.filter(Boolean);
  const subjectKeywords = groupTerms.map((list) => orGroup(list.map((t) => `subject:${t}`))).filter(Boolean);
  const inSubject = narrow && narrower.keywords_in_subject;

  // People (email) and the document terms.
  const people = (list: string[]) =>
    list.map((p) => {
      const f = formatParticipant(p);
      note(f.warning);
      return f.text;
    }).filter((t): t is string => Boolean(t));
  const sideA = people(parts.participants.side_a);
  const sideB = people(parts.participants.side_b);
  const peopleClause = andAll([orGroup(sideA), orGroup(sideB)]);
  const documentClause = orGroup(terms(parts.document_terms.terms));

  if (isValidIsoDate(dates.start) && isValidIsoDate(dates.end) && dates.start > dates.end) {
    note('The start date is after the end date, so nothing can match.');
  }
  for (const value of [dates.start, dates.end]) {
    if (value && !isValidIsoDate(value)) note(`“${value}” is not a date; it is left out.`);
  }
  const sent = dateClauses('sent', dates.start, dates.end);
  const modified = dateClauses('lastmodifiedtime', dates.start, dates.end);

  const mailKinds = [kinds.email && 'kind:email', kinds.meetings && 'kind:meetings'].filter(Boolean) as string[];
  const hasMail = mailKinds.length > 0;
  const docsWanted = kinds.documents && !(narrow && narrower.email_only && hasMail);
  // "Keywords in the subject line" is for email: documents have no subject, so
  // with both kinds the subject keywords go in the email branch and documents
  // keep the plain keywords. Otherwise the keywords stand on their own line.
  const perBranch = inSubject && hasMail && docsWanted;
  const mailBranch = hasMail
    ? andAll([orGroup(mailKinds), ...(perBranch ? subjectKeywords : []), peopleClause, ...sent])
    : '';
  const docBranch = docsWanted
    ? andAll(['kind:docs', ...(perBranch ? plainKeywords : []), documentClause, ...modified])
    : '';
  const branches = [mailBranch, docBranch].filter(Boolean);
  // Documents alone have no subject line: their keywords stay plain.
  const keywordClauses = perBranch ? [] : [...(inSubject && hasMail ? subjectKeywords : plainKeywords)];
  if (narrow) keywordClauses.push(orGroup(terms(narrower.require_terms)));
  if (branches.length === 0) note('Pick at least one item type.');

  const exclusionTerms = terms(parts.exclusions.terms);
  for (const key of Object.keys(STANDARD_EXCLUSION_CLAUSES) as (keyof StandardExclusions)[]) {
    if (standard[key]) exclusionTerms.push(STANDARD_EXCLUSION_CLAUSES[key]);
  }
  const exclusionClause = exclusionTerms.length ? `NOT (${exclusionTerms.join(' OR ')})` : '';

  const lines: string[] = [];
  const add = (clause: string) => lines.push(lines.length ? `AND ${clause}` : clause);
  keywordClauses.filter(Boolean).forEach((c) => add(c));
  if (branches.length === 1) add(wrap(branches[0]));
  if (branches.length > 1) add(`(\n  ${branches.map(wrap).join('\n  OR ')}\n)`);
  if (lines.length === 0 && exclusionClause) {
    note('A query needs something to search for, not only exclusions.');
  } else if (exclusionClause) {
    add(exclusionClause);
  }
  const keyql = lines.join('\n');

  if (keyql && plainKeywords.length === 0 && !peopleClause && !documentClause && sent.length === 0) {
    note('No keywords, people or dates: this matches every item in the sources you add.');
  }

  const chars = keyql.length;
  const operators = countOperators(keyql);
  const participants = sideA.length + sideB.length;
  const levels: QueryWarning[] = warnings.map((message) => ({ level: 'warning', message }));
  if (chars > PURVIEW_LIMITS.chars) {
    levels.unshift({ level: 'error', message: `Purview takes up to ${PURVIEW_LIMITS.chars.toLocaleString()} characters. Remove terms or split the search in two.` });
  } else if (chars >= PURVIEW_LIMITS.chars * PURVIEW_LIMITS.nearShare) {
    levels.unshift({ level: 'warning', message: `Close to Purview's ${PURVIEW_LIMITS.chars.toLocaleString()}-character limit.` });
  }
  if (operators > PURVIEW_LIMITS.operators) {
    levels.unshift({ level: 'error', message: `Purview takes up to ${PURVIEW_LIMITS.operators} operators (AND, OR, NOT, NEAR). Remove terms or split the search in two.` });
  } else if (operators >= PURVIEW_LIMITS.operators * PURVIEW_LIMITS.nearShare) {
    levels.unshift({ level: 'warning', message: `Close to Purview's limit of ${PURVIEW_LIMITS.operators} operators.` });
  }
  if (participants > PURVIEW_LIMITS.participants) {
    levels.unshift({
      level: 'warning',
      message: `${participants} participants: Purview takes ${PURVIEW_LIMITS.participants} (${PURVIEW_LIMITS.participantsPremium} with eDiscovery Premium).`,
    });
  }

  return {
    keyql,
    chars,
    operators,
    participants,
    warnings: levels,
    fragments: {
      keywords: keywordFragments,
      people: peopleClause,
      documents: documentClause,
      dates: [andAll(sent), andAll(modified)].filter(Boolean).join('   ·   '),
      types: orGroup([...mailKinds, kinds.documents ? 'kind:docs' : ''].filter(Boolean)),
      exclusions: exclusionClause,
    },
  };
}

/**
 * The builder's editable form of the parts: list fields as the comma-separated
 * text the user types (so typing a comma doesn't lose it), split again on build.
 */
export interface EditableParts {
  groups: { label: string; text: string; reason: string }[];
  sideA: string;
  sideB: string;
  peopleReason: string;
  documentTerms: string;
  documentReason: string;
  start: string;
  end: string;
  dateReason: string;
  email: boolean;
  documents: boolean;
  meetings: boolean;
  typesReason: string;
  exclusionTerms: string;
  exclusionReason: string;
  keywordsInSubject: boolean;
  emailOnly: boolean;
  requireTerms: string;
  narrowerReason: string;
}

const join = (list: string[]) => list.join(', ');

export function toEditable(parts: PurviewQueryParts): EditableParts {
  return {
    groups: parts.keyword_groups.map((g) => ({ label: g.label, text: join(g.terms), reason: g.reason })),
    sideA: join(parts.participants.side_a),
    sideB: join(parts.participants.side_b),
    peopleReason: parts.participants.reason,
    documentTerms: join(parts.document_terms.terms),
    documentReason: parts.document_terms.reason,
    start: parts.date_range.start ?? '',
    end: parts.date_range.end ?? '',
    dateReason: parts.date_range.reason,
    email: parts.item_types.email,
    documents: parts.item_types.documents,
    meetings: parts.item_types.meetings,
    typesReason: parts.item_types.reason,
    exclusionTerms: join(parts.exclusions.terms),
    exclusionReason: parts.exclusions.reason,
    keywordsInSubject: parts.narrower.keywords_in_subject,
    emailOnly: parts.narrower.email_only,
    requireTerms: join(parts.narrower.require_terms),
    narrowerReason: parts.narrower.reason,
  };
}

export function fromEditable(edit: EditableParts): PurviewQueryParts {
  return {
    keyword_groups: edit.groups.map((g) => ({ label: g.label, terms: splitTerms(g.text), reason: g.reason })),
    participants: { side_a: splitTerms(edit.sideA), side_b: splitTerms(edit.sideB), reason: edit.peopleReason },
    document_terms: { terms: splitTerms(edit.documentTerms), reason: edit.documentReason },
    date_range: { start: edit.start || null, end: edit.end || null, reason: edit.dateReason },
    item_types: { email: edit.email, documents: edit.documents, meetings: edit.meetings, reason: edit.typesReason },
    exclusions: { terms: splitTerms(edit.exclusionTerms), reason: edit.exclusionReason },
    narrower: {
      keywords_in_subject: edit.keywordsInSubject,
      email_only: edit.emailOnly,
      require_terms: splitTerms(edit.requireTerms),
      reason: edit.narrowerReason,
    },
  };
}

/** `"20th Street SE" NEAR(10) Northline` from the first keyword and the first document term, or null. */
export function nearExample(parts: PurviewQueryParts): string | null {
  const keyword = parts.keyword_groups.flatMap((g) => g.terms).map((t) => formatTerm(t).text).find(Boolean);
  const other = [...parts.document_terms.terms, ...parts.narrower.require_terms]
    .map((t) => formatTerm(t).text)
    .find((t) => t && t !== keyword);
  return keyword && other ? `${keyword} NEAR(10) ${other}` : null;
}
